// File: src/pages/mentor-park/CareerToolsPreparationPage.jsx
// Description: Scaffold for Career Tools & Preparation

import React, { useState, useContext, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Typography, Paper, Tabs, Tab, Grid, TextField, Button, Stack, Divider, Chip, LinearProgress, useTheme, useMediaQuery, MenuItem, Select, InputLabel, FormControl, CircularProgress, Avatar, IconButton, Tooltip } from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import AssessmentIcon from '@mui/icons-material/Assessment';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import InsightsIcon from '@mui/icons-material/Insights';
import PsychologyIcon from '@mui/icons-material/Psychology';
import ContentCopy from '@mui/icons-material/ContentCopy';
import { Mic, MicOff } from '@mui/icons-material';
import AIService from '../../services/AIService';
import { AuthContext } from '../../context/AuthContext';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const sections = [
  'Resume Enhancer',
  'Job-Ad Alignment',
  'Cover Letter',
  'Mock Interviews',
  'Interview Insights',
  'Confidence Coach'
];

const sectionIcons = [
  <DescriptionIcon fontSize="small" />, // Resume Enhancer
  <AssessmentIcon fontSize="small" />,  // Job-Ad Alignment
  <MailOutlineIcon fontSize="small" />, // Cover Letter
  <RecordVoiceOverIcon fontSize="small" />, // Mock Interviews
  <InsightsIcon fontSize="small" />,    // Interview Insights
  <PsychologyIcon fontSize="small" />   // Confidence Coach
];

const simulateDelay = (ms) => new Promise((res) => setTimeout(res, ms));

// Lightweight markdown renderer for headings, lists, and bold/italics
const escapeHtml = (str) =>
  str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

const renderInline = (text) => {
  let escaped = escapeHtml(text);
  // bold **text** or __text__
  escaped = escaped.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  escaped = escaped.replace(/__(.*?)__/g, '<strong>$1</strong>');
  // italics *text* or _text_
  escaped = escaped.replace(/\*(.*?)\*/g, '<em>$1</em>');
  escaped = escaped.replace(/_(.*?)_/g, '<em>$1</em>');
  return escaped;
};

const renderMarkdownToElements = (markdown, theme) => {
  if (!markdown || typeof markdown !== 'string') return null;
  const lines = markdown.split(/\r?\n/);
  const elements = [];
  let listBuffer = [];

  const flushList = () => {
    if (listBuffer.length === 0) return;
    elements.push(
      <Box key={`list-${elements.length}`} component="ul" sx={{ pl: 2, my: 1 }}>
        {listBuffer.map((item, idx) => (
          <li key={idx} dangerouslySetInnerHTML={{ __html: renderInline(item) }} />
        ))}
      </Box>
    );
    listBuffer = [];
  };

  lines.forEach((raw, idx) => {
    const line = raw.trim();
    if (line === '') {
      flushList();
      elements.push(<Box key={`sp-${idx}`} sx={{ height: 8 }} />);
      return;
    }

    const bulletMatch = /^[-*]\s+(.+)$/.exec(line);
    const orderedMatch = /^(\d+)\.\s+(.+)$/.exec(line);
    if (bulletMatch) {
      listBuffer.push(bulletMatch[1]);
      return;
    }
    if (orderedMatch) {
      listBuffer.push(orderedMatch[2]);
      return;
    }

    flushList();

    if (line.startsWith('### ')) {
      elements.push(
        <Typography key={`h3-${idx}`} variant="h6" sx={{ fontWeight: 700, mt: 1 }} dangerouslySetInnerHTML={{ __html: renderInline(line.slice(4)) }} />
      );
      return;
    }
    if (line.startsWith('## ')) {
      elements.push(
        <Typography key={`h2-${idx}`} variant="h5" sx={{ fontWeight: 700, mt: 1 }} dangerouslySetInnerHTML={{ __html: renderInline(line.slice(3)) }} />
      );
      return;
    }
    if (line.startsWith('# ')) {
      elements.push(
        <Typography key={`h1-${idx}`} variant="h4" sx={{ fontWeight: 700, mt: 1 }} dangerouslySetInnerHTML={{ __html: renderInline(line.slice(2)) }} />
      );
      return;
    }

    elements.push(
      <Typography key={`p-${idx}`} variant="body2" sx={{ lineHeight: 1.7 }} dangerouslySetInnerHTML={{ __html: renderInline(line) }} />
    );
  });

  flushList();
  return elements;
};

// Tab Components
const ResumeEnhancerTab = ({ loading, result, onBuild, copyToClipboard, jobUrl, jobDesc, resume, setJobUrl, setJobDesc, setResume }) => {
  const theme = useTheme();
  const contentRef = useRef(null);

  const handleDownloadPDF = async () => {
    if (!contentRef.current) return;
    const node = contentRef.current;
    const canvas = await html2canvas(node, { scale: 2, useCORS: true, backgroundColor: '#ffffff' });
    const imgData = canvas.toDataURL('image/png');

    const pdf = new jsPDF('p', 'pt', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = pageWidth - 40; // margins
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    let position = 20;
    let heightLeft = imgHeight;

    pdf.addImage(imgData, 'PNG', 20, position, imgWidth, imgHeight);
    heightLeft -= pageHeight - 40;

    while (heightLeft > 0) {
      pdf.addPage();
      position = 20 - (imgHeight - heightLeft);
      pdf.addImage(imgData, 'PNG', 20, position, imgWidth, imgHeight);
      heightLeft -= pageHeight - 40;
    }

    pdf.save('Generated_Resume.pdf');
  };

  const formatted = result?.resumeText ? renderMarkdownToElements(result.resumeText, theme) : null;

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={5}>
        <Paper sx={{ p: 2 }}>
          <Stack spacing={2}>
            <TextField label="Job Ad URL (optional)" value={jobUrl} onChange={(e) => setJobUrl(e.target.value)} size="small" />
            <TextField label="Job Ad Description" value={jobDesc} onChange={(e) => setJobDesc(e.target.value)} multiline minRows={4} />
            <TextField label="Resume or Key Skills" placeholder="Paste your resume or list key skills..." value={resume} onChange={(e) => setResume(e.target.value)} multiline minRows={6} />
            <Button
              variant="contained"
              disabled={loading || (!jobUrl && !jobDesc) || !resume}
              onClick={() => onBuild({ jobUrl, jobDesc, resumeOrSkills: resume })}
            >
              Generate Resume
            </Button>
            {loading && <LinearProgress />}
          </Stack>
        </Paper>
      </Grid>
      <Grid item xs={12} md={7}>
        <Paper sx={{ p: 2 }}>
          {result ? (
            <Stack spacing={2}>
              {result.resumeText && (
                <>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>Generated Resume</Typography>
                  <Paper ref={contentRef} sx={{ p: 2, bgcolor: theme.palette.background.default }}>
                    <Box>
                      {formatted}
                    </Box>
                  </Paper>
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button size="small" startIcon={<ContentCopy />} onClick={() => copyToClipboard(result.resumeText)}>Copy</Button>
                    <Button size="small" onClick={handleDownloadPDF}>Download PDF</Button>
                  </Stack>
                  <Divider />
                </>
              )}
              {result.score != null && (
                <>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>ATS Summary</Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body1">Estimated ATS score:</Typography>
                    <Chip label={`${result.score}/100`} color={result.score >= 80 ? 'success' : result.score >= 60 ? 'warning' : 'default'} />
                  </Stack>
                  <Divider />
                </>
              )}
              {Array.isArray(result.keywords) && result.keywords.length > 0 && (
                <>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Keyword Coverage</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {result.keywords.map((k, i) => (<Chip key={i} label={k} variant="outlined" />))}
                  </Box>
                  <Divider />
                </>
              )}
              {Array.isArray(result.suggestions) && result.suggestions.length > 0 && (
                <>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Suggestions</Typography>
                  <Stack spacing={0.5}>
                    {result.suggestions.map((s, i) => (<Typography key={i} variant="body2">• {s}</Typography>))}
                  </Stack>
                  <Divider />
                </>
              )}
              {Array.isArray(result.improvedBullets) && result.improvedBullets.length > 0 && (
                <>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Improved Bullets</Typography>
                  <Stack spacing={1}>
                    {result.improvedBullets.map((b, i) => (
                      <Paper key={i} sx={{ p: 1.5, bgcolor: theme.palette.background.default }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
                          <Typography variant="body2">{b}</Typography>
                          <Button size="small" startIcon={<ContentCopy />} onClick={() => copyToClipboard(b)}>Copy</Button>
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                </>
              )}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">Paste a job ad (URL or description) and your resume/skills, then generate.</Typography>
          )}
        </Paper>
      </Grid>
    </Grid>
  );
};

const JobAdAlignmentTab = ({ loading, result, onAnalyze, jobUrl, jobDesc, resume, setJobUrl, setJobDesc, setResume, onAlign }) => {
  const theme = useTheme();
  const contentRef = useRef(null);
  const formatted = result?.alignmentText ? renderMarkdownToElements(result.alignmentText, theme) : null;

  const handleDownloadPDF = async () => {
    if (!contentRef.current) return;
    const node = contentRef.current;
    const canvas = await html2canvas(node, { scale: 2, useCORS: true, backgroundColor: '#ffffff' });
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'pt', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = pageWidth - 40;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    let position = 20;
    let heightLeft = imgHeight;
    pdf.addImage(imgData, 'PNG', 20, position, imgWidth, imgHeight);
    heightLeft -= pageHeight - 40;
    while (heightLeft > 0) {
      pdf.addPage();
      position = 20 - (imgHeight - heightLeft);
      pdf.addImage(imgData, 'PNG', 20, position, imgWidth, imgHeight);
      heightLeft -= pageHeight - 40;
    }
    pdf.save('Job_Ad_Alignment.pdf');
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={5}>
        <Paper sx={{ p: 2 }}>
          <Stack spacing={2}>
            <TextField label="Job Ad URL (optional)" value={jobUrl} onChange={(e) => setJobUrl(e.target.value)} size="small" />
            <TextField label="Job Description" value={jobDesc} onChange={(e) => setJobDesc(e.target.value)} multiline minRows={6} />
            <TextField label="Resume Text" value={resume} onChange={(e) => setResume(e.target.value)} multiline minRows={6} />
            <Button
              variant="contained"
              disabled={loading || (!jobDesc && !jobUrl) || !resume}
              onClick={onAlign}
            >
              Analyze Alignment
            </Button>
            {loading && <LinearProgress />}
          </Stack>
        </Paper>
      </Grid>
      <Grid item xs={12} md={7}>
        <Paper sx={{ p: 2 }}>
          {result ? (
            <Stack spacing={2}>
              {result.alignmentText && (
                <>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>Alignment Report</Typography>
                  <Paper ref={contentRef} sx={{ p: 2, bgcolor: theme.palette.background.default }}>
                    <Box>
                      {formatted}
                    </Box>
                  </Paper>
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button size="small" startIcon={<ContentCopy />} onClick={() => navigator.clipboard.writeText(result.alignmentText)}>Copy</Button>
                    <Button size="small" onClick={handleDownloadPDF}>Download PDF</Button>
                  </Stack>
                  <Divider />
                </>
              )}
              {/* Retain structured chips if parser extracted them */}
              {Array.isArray(result.requirements) && result.requirements.length > 0 && (
                <>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Requirements</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {result.requirements.map((r, i) => (<Chip key={i} label={r} />))}
                  </Box>
                </>
              )}
              {Array.isArray(result.matched) && result.matched.length > 0 && (
                <>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Matched</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {result.matched.map((m, i) => (<Chip key={i} label={m} color="success" variant="outlined" />))}
                  </Box>
                </>
              )}
              {Array.isArray(result.gaps) && result.gaps.length > 0 && (
                <>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Gaps</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {result.gaps.map((g, i) => (<Chip key={i} label={g} color="warning" variant="outlined" />))}
                  </Box>
                </>
              )}
              {Array.isArray(result.suggestions) && result.suggestions.length > 0 && (
                <>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Suggestions</Typography>
                  <Stack spacing={0.5}>
                    {result.suggestions.map((s, i) => (<Typography key={i} variant="body2">• {s}</Typography>))}
                  </Stack>
                </>
              )}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">Paste a JD or URL with your resume, then analyze.</Typography>
          )}
        </Paper>
      </Grid>
    </Grid>
  );
};

const CoverLetterTab = ({ loading, result, copyToClipboard, jobUrl, jobDesc, resume, setJobUrl, setJobDesc, setResume, onGenerate }) => {
  const [tone, setTone] = useState('professional');
  const theme = useTheme();
  const contentRef = useRef(null);

  const handleDownloadPDF = async () => {
    if (!contentRef.current) return;
    const node = contentRef.current;
    const canvas = await html2canvas(node, { scale: 2, useCORS: true, backgroundColor: '#ffffff' });
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'pt', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = pageWidth - 40;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    let position = 20;
    let heightLeft = imgHeight;
    pdf.addImage(imgData, 'PNG', 20, position, imgWidth, imgHeight);
    heightLeft -= pageHeight - 40;
    while (heightLeft > 0) {
      pdf.addPage();
      position = 20 - (imgHeight - heightLeft);
      pdf.addImage(imgData, 'PNG', 20, position, imgWidth, imgHeight);
      heightLeft -= pageHeight - 40;
    }
    pdf.save('Cover_Letter.pdf');
  };

  const formatted = result?.coverLetterText ? renderMarkdownToElements(result.coverLetterText, theme) : null;

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={5}>
        <Paper sx={{ p: 2 }}>
          <Stack spacing={2}>
            {/* Shared inputs */}
            <TextField label="Job Ad URL (optional)" value={jobUrl} onChange={(e) => setJobUrl(e.target.value)} size="small" />
            <TextField label="Job Description" value={jobDesc} onChange={(e) => setJobDesc(e.target.value)} multiline minRows={4} />
            <TextField label="Resume or Key Skills" value={resume} onChange={(e) => setResume(e.target.value)} multiline minRows={4} />

            {/* Optional tone only */}
            <TextField label="Tone" value={tone} onChange={(e) => setTone(e.target.value)} size="small" />
            <Button
              variant="contained"
              disabled={loading || (!jobDesc && !jobUrl) || !resume}
              onClick={() => onGenerate({ jobUrl, jobDesc, resumeOrSkills: resume, tone })}
            >
              Generate Cover Letter
            </Button>
            {loading && <LinearProgress />}
          </Stack>
        </Paper>
      </Grid>
      <Grid item xs={12} md={7}>
        <Paper sx={{ p: 2 }}>
          {result ? (
            <Stack spacing={2}>
              {result.coverLetterText && (
                <>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>Cover Letter</Typography>
                  <Paper ref={contentRef} sx={{ p: 2, bgcolor: theme.palette.background.default }}>
                    <Box>
                      {formatted}
                    </Box>
                  </Paper>
                  <Stack direction="row" justifyContent="flex-end" spacing={1}>
                    <Button size="small" startIcon={<ContentCopy />} onClick={() => copyToClipboard(result.coverLetterText)}>Copy</Button>
                    <Button size="small" onClick={handleDownloadPDF}>Download PDF</Button>
                  </Stack>
                  <Divider />
                </>
              )}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">Paste a job ad (URL or description) and your resume/skills, then generate.</Typography>
          )}
        </Paper>
      </Grid>
    </Grid>
  );
};

const MockInterviewsTab = ({ loading, result, jobUrl, jobDesc, resume, setJobUrl, setJobDesc, setResume, onSimulate }) => {
  const theme = useTheme();
  const contentRef = useRef(null);
  const { user: authUser } = useContext(AuthContext);
  const [mode, setMode] = useState('practice'); // practice | simulation
  const [type, setType] = useState('HR'); // HR | Technical | Case | Panel
  const [started, setStarted] = useState(false);
  const [answer, setAnswer] = useState('');
  const [chat, setChat] = useState([]); // {role: 'AI'|'User', text}
  const [report, setReport] = useState(null); // {scores, reportText}
  const [busy, setBusy] = useState(false);
  const recognitionRef = useRef(null);
  const [sttAvailable, setSttAvailable] = useState(false);
  const [listening, setListening] = useState(false);

  const scrollToBottomRef = useRef(null);
  const scrollToBottom = () => {
    if (scrollToBottomRef.current) {
      scrollToBottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const parseSixLineResponse = (rawText) => {
    const out = { action: '', question: '', followUp: '', feedback: '', scores: {}, report: '' };
    if (!rawText) return out;
    const lines = rawText.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
    const get = (prefix) => {
      const l = lines.find(x => x.toUpperCase().startsWith(prefix));
      return l ? l.substring(l.indexOf(':') + 1).trim() : '';
    };
    out.action = get('ACTION').toLowerCase();
    out.question = get('QUESTION');
    out.followUp = get('FOLLOW_UP');
    out.feedback = get('FEEDBACK');
    const scoresLine = get('SCORES');
    if (scoresLine) {
      scoresLine.split(',').forEach(pair => {
        const [k, v] = pair.split('=').map(s => s.trim());
        if (k && v) out.scores[k] = isNaN(Number(v)) ? v : Number(v);
      });
    }
    out.report = get('REPORT');
    return out;
  };

  const addAI = (text) => setChat(prev => [...prev, { role: 'AI', text }]);
  const addUser = (text) => setChat(prev => [...prev, { role: 'User', text }]);
  const resetSession = () => {
    setStarted(false);
    setChat([]);
    setAnswer('');
    setReport(null);
  };

  const buildChatHistory = () => chat.map(m => ({ role: m.role, text: m.text }));

  const startSession = async () => {
    if (busy) return;
    setBusy(true);
    setStarted(true);
    setChat([]);
    setReport(null);

    // Intro line locally
    addAI(`Welcome to a ${type} interview. I'll ask questions one at a time. Please answer concisely. When ready, respond below.`);

    // Ask first question via API (last_user_answer empty)
    try {
      const { mockText } = await AIService.runMockInterview({
        mode,
        chatHistory: [],
        jobDescription: jobDesc || '',
        userBio: resume || '',
        lastUserAnswer: ''
      });

      const fallBack = 'ACTION: ask_question\nQUESTION: Tell me about yourself and what makes you a strong fit for this role.\nFOLLOW_UP: \nFEEDBACK: \nSCORES: \nREPORT: ';
      const parsed = parseSixLineResponse(mockText || fallBack);
      
      // Display either QUESTION or FOLLOW_UP based on ACTION, not both
      if (parsed.action === 'ask_question' && parsed.question) {
        addAI(parsed.question);
      } else if (parsed.action === 'ask_follow_up' && parsed.followUp) {
        addAI(`FOLLOW_UP: ${parsed.followUp}`);
      }
    } finally {
      setBusy(false);
      simulateDelay(50).then(scrollToBottom);
    }
  };

  const handleSend = async () => {
    if (!answer.trim() || busy) return;
    const userAns = answer.trim();
    addUser(userAns);
    setAnswer('');
    scrollToBottom();

    setBusy(true);
    try {
      const { mockText } = await AIService.runMockInterview({
        mode,
        chatHistory: buildChatHistory(),
        jobDescription: jobDesc || '',
        userBio: resume || '',
        lastUserAnswer: userAns
      });

      const parsed = parseSixLineResponse(mockText);

      // Always show feedback first if available (practice mode)
      if (mode === 'practice' && parsed.feedback) {
        addAI(`FEEDBACK: ${parsed.feedback}`);
      }

      // Then display either QUESTION or FOLLOW_UP based on ACTION, not both
      if (parsed.action === 'ask_question' && parsed.question) {
        addAI(parsed.question);
      } else if (parsed.action === 'ask_follow_up' && parsed.followUp) {
        addAI(`FOLLOW_UP: ${parsed.followUp}`);
      }

      if (parsed.scores && Object.keys(parsed.scores).length > 0 && (mode === 'practice' || parsed.action === 'finish')) {
        setReport(prev => ({ ...(prev || {}), scores: parsed.scores }));
      }

      if (parsed.action === 'finish' && parsed.report) {
        setReport(prev => ({ ...(prev || {}), reportText: parsed.report }));
        addAI('Thank you. This concludes the interview. A summary report is available below.');
      }
    } catch (e) {
      // Minimal fallback
      addAI('Panel Member: Thank you. Could you elaborate on a quantified outcome from that experience?');
    } finally {
      setBusy(false);
      simulateDelay(50).then(scrollToBottom);
    }
  };

  const downloadTranscript = async () => {
    if (!contentRef.current) return;
    const node = contentRef.current;
    const canvas = await html2canvas(node, { scale: 2, useCORS: true, backgroundColor: '#ffffff' });
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'pt', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = pageWidth - 40;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    let position = 20;
    let heightLeft = imgHeight;
    pdf.addImage(imgData, 'PNG', 20, position, imgWidth, imgHeight);
    heightLeft -= pageHeight - 40;
    while (heightLeft > 0) {
      pdf.addPage();
      position = 20 - (imgHeight - heightLeft);
      pdf.addImage(imgData, 'PNG', 20, position, imgWidth, imgHeight);
      heightLeft -= pageHeight - 40;
    }
    pdf.save('Mock_Interview_Transcript.pdf');
  };

  const userDisplayName = authUser?.name || 'You';

  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    setSttAvailable(!!SpeechRecognition);
  }, []);

  const startRecognition = () => {
    if (listening) return;
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return;
    try {
      const rec = new SpeechRecognition();
      recognitionRef.current = rec;
      rec.lang = navigator.language || 'en-US';
      rec.interimResults = true;
      rec.continuous = false;
      let finalText = '';
      rec.onresult = (event) => {
        let interim = '';
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalText += transcript + ' ';
          } else {
            interim += transcript;
          }
        }
        const combined = (answer ? answer + ' ' : '') + (finalText + interim).trim();
        setAnswer(combined);
      };
      rec.onerror = () => {
        setListening(false);
      };
      rec.onend = () => {
        setListening(false);
      };
      rec.start();
      setListening(true);
    } catch (e) {
      setListening(false);
    }
  };

  const stopRecognition = () => {
    try { recognitionRef.current && recognitionRef.current.stop(); } catch {}
    setListening(false);
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={5}>
        <Paper sx={{ p: 2 }}>
          <Stack spacing={2}>
            {/* Shared inputs */}
            <TextField label="Job Ad Description" value={jobDesc} onChange={(e) => setJobDesc(e.target.value)} multiline minRows={4} />
            <TextField label="User Bio / Resume" value={resume} onChange={(e) => setResume(e.target.value)} multiline minRows={4} />

            {/* Controls */}
            <FormControl size="small">
              <InputLabel id="int-type-label">Interview Type</InputLabel>
              <Select labelId="int-type-label" label="Interview Type" value={type} onChange={(e) => setType(e.target.value)}>
                <MenuItem value="HR">HR Screening</MenuItem>
                <MenuItem value="Technical">Technical / Skill-Based</MenuItem>
                <MenuItem value="Case">Case Study / Problem-Solving</MenuItem>
                <MenuItem value="Panel">Panel Simulation (single-voice, varied tone)</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small">
              <InputLabel id="mode-label">Mode</InputLabel>
              <Select labelId="mode-label" label="Mode" value={mode} onChange={(e) => setMode(e.target.value)}>
                <MenuItem value="practice">Practice (immediate feedback)</MenuItem>
                <MenuItem value="simulation">Simulation (end report)</MenuItem>
              </Select>
            </FormControl>

            <Stack direction="row" spacing={1}>
              <Button variant="contained" onClick={startSession} disabled={started || busy}>Start</Button>
              <Button variant="outlined" onClick={resetSession} disabled={busy}>Reset</Button>
              <Button variant="text" onClick={downloadTranscript} disabled={!started}>Download Transcript</Button>
            </Stack>
          </Stack>
        </Paper>
      </Grid>

      <Grid item xs={12} md={7}>
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', minHeight: 480 }}>
          {/* Chat */}
          <Box ref={contentRef} sx={{ flex: 1, overflowY: 'auto', pr: 1 }}>
            {chat.length === 0 && (
              <Typography variant="body2" color="text.secondary">Start the session to receive your first question.</Typography>
            )}
            <Stack spacing={1.25}>
              {chat.map((m, idx) => {
                const isUser = m.role === 'User';
                const author = isUser ? userDisplayName : 'Panel Member';
                return (
                  <Box key={idx} sx={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start' }}>
                    <Stack direction="row" spacing={1} sx={{ maxWidth: '100%', alignItems: 'flex-start' }}>
                      {!isUser && (
                        <Avatar sx={{ width: 28, height: 28, bgcolor: theme.palette.grey[300] }}>P</Avatar>
                      )}
                      <Paper sx={{ p: 1.25, bgcolor: isUser ? theme.palette.primary.light + '22' : theme.palette.background.default, borderRadius: 2, boxShadow: '0 2px 6px rgba(0,0,0,0.06)', border: `1px solid ${theme.palette.divider}` }}>
                        <Stack spacing={0.5}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Chip label={author} size="small" variant={isUser ? 'filled' : 'outlined'} color={isUser ? 'primary' : 'default'} />
                          </Stack>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{m.text}</Typography>
                        </Stack>
                      </Paper>
                    </Stack>
                  </Box>
                );
              })}
              {started && busy && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <CircularProgress size={16} />
                  <Typography variant="caption" color="text.secondary">Panel Member is processing your answer…</Typography>
                </Box>
              )}
              <div ref={scrollToBottomRef} />
            </Stack>
          </Box>

          {/* Composer with voice input */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1 }}>
            <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
              <TextField
                placeholder={started ? (sttAvailable ? (listening ? 'Listening… speak now' : 'Type or use the mic…') : 'Type your answer…') : 'Click Start to begin'}
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                disabled={!started || busy}
                fullWidth
                multiline
                minRows={2}
              />
              <Tooltip title={sttAvailable ? (listening ? 'Stop listening' : 'Start voice input') : 'Voice input not supported on this browser'}>
                <span>
                  <IconButton color={listening ? 'error' : 'primary'} onClick={listening ? stopRecognition : startRecognition} disabled={!started || busy || !sttAvailable} sx={{ alignSelf: 'center' }}>
                    {listening ? <MicOff /> : <Mic />}
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
            <Button variant="contained" onClick={handleSend} disabled={!started || busy || !answer.trim()}>Send</Button>
          </Stack>
          {started && sttAvailable && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
              Tip: tap the mic to dictate; tap again to stop.
            </Typography>
          )}

          {/* Report (if any) */}
          {report && (
            <Box sx={{ mt: 2 }}>
              <Divider sx={{ mb: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>Session Summary</Typography>
              {report.scores && (
                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1 }}>
                  {Object.entries(report.scores).map(([k, v]) => (
                    <Chip key={k} label={`${k}: ${v}`} size="small" />
                  ))}
                </Stack>
              )}
              {report.reportText && (
                <Paper sx={{ p: 2, bgcolor: theme.palette.background.default }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{report.reportText}</Typography>
                </Paper>
              )}
            </Box>
          )}
        </Paper>
      </Grid>
    </Grid>
  );
};

const CareerToolsPreparationPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useContext(AuthContext);
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [output, setOutput] = useState({});

  // Set initial tab from query parameter
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam !== null) {
      const tabIndex = parseInt(tabParam, 10);
      if (!isNaN(tabIndex) && tabIndex >= 0 && tabIndex < sections.length) {
        setTab(tabIndex);
      }
    }
  }, [searchParams]);

  // Shared inputs across tabs
  const [sharedJobUrl, setSharedJobUrl] = useState('');
  const [sharedJobDesc, setSharedJobDesc] = useState('');
  const [sharedResume, setSharedResume] = useState(user?.bio || '');

  const handleAnalyze = async (index, generator) => {
    setLoading(true);
    await simulateDelay(800);
    setOutput((prev) => ({ ...prev, [index]: generator() }));
    setLoading(false);
  };

  const handleResumeBuild = async ({ jobUrl, jobDesc, resumeOrSkills }) => {
    setLoading(true);
    try {
      const profile = {
        name: user?.name || 'Professional',
        jobTitle: user?.jobTitle || 'Not specified',
        industry: user?.industry || 'General',
        experience: user?.experience || 'Various',
        location: user?.location || 'Global',
        skills: user?.skills || ['Networking', 'Communication'],
        bio: user?.bio || 'Professional seeking opportunities',
        education: user?.education || 'Not specified',
        currentCompany: user?.currentCompany || 'Not specified'
      };
      const resp = await AIService.buildResume(profile, { jobUrl, jobDesc, resumeOrSkills });
      setOutput((prev) => ({ ...prev, 0: resp }));
    } catch (e) {
      setOutput((prev) => ({ ...prev, 0: { resumeText: 'Failed to generate resume. Please try again.' } }));
    } finally {
      setLoading(false);
    }
  };

  const handleJobAdAlign = async () => {
    setLoading(true);
    try {
      const profile = {
        name: user?.name || 'Professional',
        jobTitle: user?.jobTitle || 'Not specified',
        industry: user?.industry || 'General',
        experience: user?.experience || 'Various',
        location: user?.location || 'Global',
        skills: user?.skills || ['Networking', 'Communication'],
        bio: user?.bio || 'Professional seeking opportunities',
        education: user?.education || 'Not specified',
        currentCompany: user?.currentCompany || 'Not specified'
      };
      const resp = await AIService.alignJobAd(profile, { jobUrl: sharedJobUrl, jobDesc: sharedJobDesc, resumeOrSkills: sharedResume });
      setOutput((prev) => ({ ...prev, 1: resp }));
    } catch (e) {
      setOutput((prev) => ({ ...prev, 1: { requirements: [], matched: [], gaps: [], suggestions: ['Failed to analyze job alignment.'] } }));
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateCoverLetter = async ({ jobUrl, jobDesc, resumeOrSkills, tone }) => {
    setLoading(true);
    try {
      const profile = {
        name: user?.name || 'Professional',
        jobTitle: user?.jobTitle || 'Not specified',
        industry: user?.industry || 'General',
        experience: user?.experience || 'Various',
        location: user?.location || 'Global',
        skills: user?.skills || ['Networking', 'Communication'],
        bio: user?.bio || 'Professional seeking opportunities',
        education: user?.education || 'Not specified',
        currentCompany: user?.currentCompany || 'Not specified'
      };
      const resp = await AIService.generateCoverLetter(profile, { jobUrl, jobDesc, resumeOrSkills, tone });
      setOutput((prev) => ({ ...prev, 2: resp }));
    } catch (e) {
      setOutput((prev) => ({ ...prev, 2: { coverLetterText: 'Failed to generate cover letter. Please try again.' } }));
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text) => {
    try { await navigator.clipboard.writeText(text); } catch {}
  };

  const renderInterviewInsights = () => (
    <Paper sx={{ p: 2 }}>
      <Typography variant="body2" color="text.secondary">Company/role insights UI will be added here.</Typography>
    </Paper>
  );

  const renderConfidenceCoach = () => (
    <Paper sx={{ p: 2 }}>
      <Typography variant="body2" color="text.secondary">Confidence coach practice area will be added here.</Typography>
    </Paper>
  );

  const renderTab = () => {
    switch (tab) {
      case 0: return (
        <ResumeEnhancerTab
          loading={loading}
          result={output[0]}
          onBuild={handleResumeBuild}
          copyToClipboard={copyToClipboard}
          jobUrl={sharedJobUrl}
          jobDesc={sharedJobDesc}
          resume={sharedResume}
          setJobUrl={setSharedJobUrl}
          setJobDesc={setSharedJobDesc}
          setResume={setSharedResume}
        />
      );
      case 1: return (
        <JobAdAlignmentTab
          loading={loading}
          result={output[1]}
          onAnalyze={(gen) => handleAnalyze(1, gen)}
          jobUrl={sharedJobUrl}
          jobDesc={sharedJobDesc}
          resume={sharedResume}
          setJobUrl={setSharedJobUrl}
          setJobDesc={setSharedJobDesc}
          setResume={setSharedResume}
          onAlign={handleJobAdAlign}
        />
      );
      case 2: return (
        <CoverLetterTab
          loading={loading}
          result={output[2]}
          copyToClipboard={copyToClipboard}
          jobUrl={sharedJobUrl}
          jobDesc={sharedJobDesc}
          resume={sharedResume}
          setJobUrl={setSharedJobUrl}
          setJobDesc={setSharedJobDesc}
          setResume={setSharedResume}
          onGenerate={handleGenerateCoverLetter}
        />
      );
      case 3: return (
        <MockInterviewsTab
          loading={loading}
          result={output[3]}
          jobUrl={sharedJobUrl}
          jobDesc={sharedJobDesc}
          resume={sharedResume}
          setJobUrl={setSharedJobUrl}
          setJobDesc={setSharedJobDesc}
          setResume={setSharedResume}
        />
      );
      case 4: return renderInterviewInsights();
      case 5: return renderConfidenceCoach();
      default: return null;
    }
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>Career Tools & Preparation</Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
        Resume enhancement, job alignment, cover letters, interviews, and confidence coaching
      </Typography>

      {/* Quick access buttons for high visibility on mobile (only mobile) */}
      {isMobile && (
        <Box sx={{ mb: 1.5 }}>
          <Grid container spacing={1}>
            {sections.map((s, i) => (
              <Grid key={s} item xs={6}>
                <Button
                  fullWidth
                  size="small"
                  variant={tab === i ? 'contained' : 'outlined'}
                  startIcon={sectionIcons[i]}
                  onClick={() => setTab(i)}
                  sx={{ textTransform: 'none', borderRadius: 2 }}
                >
                  {s}
                </Button>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Sticky tabs bar for easy navigation while scrolling (desktop/tablet only) */}
      {!isMobile && (
        <Paper sx={{ p: 1, mb: 2, position: 'sticky', top: 56, zIndex: 5 }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            variant={'standard'}
            textColor="primary"
            indicatorColor="primary"
          >
            {sections.map((s, i) => (
              <Tab key={s} value={i} label={s} icon={sectionIcons[i]} iconPosition="start" sx={{ minHeight: 44 }} />
            ))}
          </Tabs>
        </Paper>
      )}

      {renderTab()}
    </Box>
  );
};

export default CareerToolsPreparationPage;


